/**
 * Detecção de plataforma e de instalação da PWA.
 *
 * É a peça mais crítica do onboarding. No iOS o Web Push só existe para
 * web apps abertos pela tela de início: numa aba do Safari o
 * `PushManager` sequer está disponível, e pedir permissão ali falha em
 * silêncio — a participante ficaria 28 dias sem receber um lembrete e
 * ninguém perceberia. Por isso o app trata a instalação como etapa
 * obrigatória, e não como convite.
 */

export type Plataforma = 'ios' | 'android' | 'desktop'

interface NavigatorIOS extends Navigator {
  /** Só existe no Safari do iOS. */
  standalone?: boolean
}

export interface EventoInstalacao extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function detectarPlataforma(): Plataforma {
  const ua = navigator.userAgent

  if (/android/i.test(ua)) return 'android'

  // iPadOS 13+ se apresenta como Mac. O que o denuncia é o touch.
  const ehIOS = /iphone|ipad|ipod/i.test(ua)
  const ehIPadDisfarcado = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1
  if (ehIOS || ehIPadDisfarcado) return 'ios'

  return 'desktop'
}

/** `true` quando o app foi aberto pelo ícone, não por uma aba do navegador. */
export function estaInstalado() {
  const porDisplayMode = window.matchMedia('(display-mode: standalone)').matches
  const porIOS = (navigator as NavigatorIOS).standalone === true
  return porDisplayMode || porIOS
}

/** O navegador tem as APIs necessárias para Web Push. */
export function suportaPush() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/**
 * Podemos pedir permissão de notificação agora?
 *
 * No iOS, só depois de instalado. Nas demais plataformas, basta o
 * suporte às APIs.
 */
export function podePedirPermissao() {
  if (!suportaPush()) return false
  if (detectarPlataforma() === 'ios') return estaInstalado()
  return true
}

export function permissaoAtual(): NotificationPermission | 'indisponivel' {
  if (!('Notification' in window)) return 'indisponivel'
  return Notification.permission
}

/**
 * Guarda o `beforeinstallprompt` para disparar depois, num toque da
 * usuária. O evento só existe no Chrome/Edge — o iOS nunca o emite,
 * e é por isso que ele precisa do tutorial visual.
 */
let eventoAdiado: EventoInstalacao | null = null

export function observarConviteDeInstalacao(aoMudar: (disponivel: boolean) => void) {
  const aoReceber = (evento: Event) => {
    evento.preventDefault()
    eventoAdiado = evento as EventoInstalacao
    aoMudar(true)
  }

  const aoInstalar = () => {
    eventoAdiado = null
    aoMudar(false)
  }

  window.addEventListener('beforeinstallprompt', aoReceber)
  window.addEventListener('appinstalled', aoInstalar)

  return () => {
    window.removeEventListener('beforeinstallprompt', aoReceber)
    window.removeEventListener('appinstalled', aoInstalar)
  }
}

/** Abre o diálogo nativo de instalação. Retorna se a usuária aceitou. */
export async function convidarParaInstalar() {
  if (!eventoAdiado) return false
  await eventoAdiado.prompt()
  const { outcome } = await eventoAdiado.userChoice
  eventoAdiado = null
  return outcome === 'accepted'
}

export function temConviteDeInstalacao() {
  return eventoAdiado !== null
}
